import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    (async () => {
      if (!sessionId) {
        navigate("/login", { replace: true });
        return;
      }
      try {
        await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the hash so it isn't reprocessed
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        navigate("/app", { replace: true });
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || "Google sign-in failed.";
        setError(msg);
        setTimeout(() => navigate(`/login?error=${encodeURIComponent(msg)}`, { replace: true }), 2200);
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#05080D] text-white flex flex-col items-center justify-center gap-6" data-testid="auth-callback">
      <Logo to="/" />
      {error ? (
        <p className="max-w-sm text-center text-sm text-red-300 px-6" data-testid="auth-callback-error">{error}</p>
      ) : (
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin text-[#4F8CFF]" />
          <span className="text-sm">Signing you in…</span>
        </div>
      )}
    </div>
  );
}
